import React, { useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Users, TrendingUp, DollarSign, Layers } from 'lucide-react';
import { PERSONA_COLORS } from '../../utils/personaColors';

const REVENUE_COLORS = {
  'Management Fees': '#10b981',
  'OC Disbursements Recovered': '#3b82f6',
  'OC Fee for Service': '#8b5cf6',
  'Chargeable Meetings': '#f59e0b',
  'Tax Compliance Preparation': '#ef4444',
  'Debt Recovery Admin Charge': '#ec4899',
  'OC Certificate application charge': '#06b6d4',
  'OC Insurance Commission': '#6366f1',
  'Commission Actual': '#a855f7',
};

const CohortRevenueAnalysis = ({ groups }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Aggregate data by cohort
  const cohortData = useMemo(() => {
    const cohorts = {};

    groups.forEach(group => {
      const cohort = group.dominantPersona || 'Unknown';
      
      if (!cohorts[cohort]) {
        cohorts[cohort] = {
          name: cohort,
          groupCount: 0,
          totalRevenue: 0,
          totalWorkload: 0,
          revenueBreakdown: {},
        };
      }

      cohorts[cohort].groupCount += 1;
      cohorts[cohort].totalRevenue += group.totalRevenue || 0;
      cohorts[cohort].totalWorkload += group.totalWorkload || 0;

      // Aggregate revenue breakdown
      const breakdown = group.revenueBreakdown || {};
      Object.entries(breakdown).forEach(([key, value]) => {
        cohorts[cohort].revenueBreakdown[key] = (cohorts[cohort].revenueBreakdown[key] || 0) + value;
      });
    });

    // Calculate margins and format data
    return Object.values(cohorts)
      .map(c => ({
        ...c,
        profit: c.totalRevenue - c.totalWorkload,
        margin: c.totalRevenue > 0 ? ((c.totalRevenue - c.totalWorkload) / c.totalRevenue * 100) : 0,
        avgRevenue: c.groupCount > 0 ? c.totalRevenue / c.groupCount : 0,
      }))
      .sort((a, b) => b.totalRevenue - a.totalRevenue);
  }, [groups]);

  // Prepare data for stacked bar chart
  const breakdownChartData = useMemo(() => {
    return cohortData.map(cohort => {
      const total = cohort.totalRevenue || 1;
      const data = {
        name: cohort.name,
        totalRevenue: cohort.totalRevenue,
        groupCount: cohort.groupCount,
      };

      Object.entries(cohort.revenueBreakdown).forEach(([key, value]) => {
        data[key] = (value / total) * 100;
        data[`${key}_value`] = value;
      });

      return data;
    });
  }, [cohortData]);

  // Revenue distribution pie chart data
  const revenueDistribution = useMemo(() => {
    return cohortData.map(c => ({
      name: c.name,
      value: c.totalRevenue,
      color: PERSONA_COLORS[c.name] || '#64748b',
    }));
  }, [cohortData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div className={`p-3 rounded-xl border shadow-xl ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
          <p className={`text-sm font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {label}
          </p>
          <p className={`text-xs mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {data.groupCount} groups | Total: ${data.totalRevenue?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
          <div className="space-y-1 mt-2">
            {payload.map((entry) => {
              if (entry.value <= 0) return null;
              const valueKey = `${entry.dataKey}_value`;
              const dollarValue = entry.payload[valueKey];
              
              return (
                <div key={entry.dataKey || entry.name} className="flex items-center justify-between gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                    <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>{entry.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                      ${dollarValue?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="font-bold w-10 text-right" style={{ color: entry.color }}>
                      {entry.value.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const totalRev = cohortData.reduce((sum, c) => sum + c.totalRevenue, 0);
      const pct = totalRev > 0 ? (data.value / totalRev * 100) : 0;
      
      return (
        <div className={`p-3 rounded-xl border shadow-xl ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
          <p className={`text-sm font-bold mb-1`} style={{ color: data.color }}>{data.name}</p>
          <p className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            ${data.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {pct.toFixed(1)}% of total revenue
          </p>
        </div>
      );
    }
    return null;
  };

  const totalRevenue = cohortData.reduce((sum, c) => sum + c.totalRevenue, 0);
  const totalProfit = cohortData.reduce((sum, c) => sum + c.profit, 0);

  return (
    <div className={`rounded-2xl border transition-colors duration-300 ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
      {/* Header */}
      <div className="p-6 border-b border-white/5">
        <h3 className={`text-lg font-bold mb-1 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <Users className="w-5 h-5 mr-2 text-purple-500" />
          Revenue Analysis by Client Cohort
        </h3>
        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          Portfolio revenue breakdown aggregated by strategic cohort classification
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 border-b border-white/5">
        {cohortData.slice(0, 4).map((cohort) => (
          <div 
            key={cohort.name}
            className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-slate-50'}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERSONA_COLORS[cohort.name] }} />
              <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {cohort.name}
              </span>
            </div>
            <p className="text-lg font-bold" style={{ color: PERSONA_COLORS[cohort.name] }}>
              ${(cohort.totalRevenue / 1000000).toFixed(2)}M
            </p>
            <div className="flex items-center justify-between mt-1">
              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {cohort.groupCount} groups
              </span>
              <span className={`text-xs font-medium ${cohort.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {cohort.margin >= 0 ? '+' : ''}{cohort.margin.toFixed(1)}% gross margin
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
        {/* Revenue Distribution Pie */}
        <div className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-slate-50'}`}>
          <h4 className={`text-sm font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Revenue by Cohort
          </h4>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={revenueDistribution}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                >
                  {revenueDistribution.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="text-center mt-2">
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Portfolio</p>
            <p className="text-lg font-bold text-emerald-500">${(totalRevenue / 1000000).toFixed(2)}M</p>
          </div>
        </div>

        {/* Cohort Stats Table */}
        <div className={`col-span-2 p-4 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-slate-50'}`}>
          <h4 className={`text-sm font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Cohort Performance Summary
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-xs uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  <th className="text-left py-2 px-2">Cohort</th>
                  <th className="text-center py-2 px-2">Groups</th>
                  <th className="text-right py-2 px-2">Revenue</th>
                  <th className="text-right py-2 px-2">Workload</th>
                  <th className="text-right py-2 px-2">Gross Profit</th>
                  <th className="text-right py-2 px-2">Gross Margin</th>
                </tr>
              </thead>
              <tbody className={isDark ? 'text-slate-300' : 'text-slate-600'}>
                {cohortData.map((cohort) => (
                  <tr key={cohort.name} className={`border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PERSONA_COLORS[cohort.name] }} />
                        <span className="font-medium" style={{ color: PERSONA_COLORS[cohort.name] }}>{cohort.name}</span>
                      </div>
                    </td>
                    <td className="text-center py-2 px-2">{cohort.groupCount}</td>
                    <td className="text-right py-2 px-2 font-medium text-emerald-500">
                      ${(cohort.totalRevenue / 1000).toFixed(0)}k
                    </td>
                    <td className="text-right py-2 px-2 text-red-500">
                      ${(cohort.totalWorkload / 1000).toFixed(0)}k
                    </td>
                    <td className={`text-right py-2 px-2 font-medium ${cohort.profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {cohort.profit >= 0 ? '+' : ''}${(cohort.profit / 1000).toFixed(0)}k
                    </td>
                    <td className={`text-right py-2 px-2 font-bold ${cohort.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {cohort.margin.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Revenue Breakdown by Cohort Chart */}
      <div className="p-6 border-t border-white/5">
        <h4 className={`text-sm font-bold mb-1 flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <DollarSign className="w-4 h-4 mr-2 text-emerald-500" />
          Revenue Composition by Cohort
        </h4>
        <p className={`text-xs mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          Breakdown of revenue sources for each strategic cohort (% of cohort total)
        </p>
        <div className="h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={breakdownChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} 
                axisLine={false} 
                tickLine={false}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                unit="%" 
                tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} 
                axisLine={false} 
                tickLine={false} 
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }} />
              <Legend wrapperStyle={{ paddingTop: '10px', fontSize: '10px' }} />
              
              {Object.keys(REVENUE_COLORS).map((key) => (
                <Bar 
                  key={key} 
                  dataKey={key} 
                  stackId="a" 
                  fill={REVENUE_COLORS[key]} 
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default CohortRevenueAnalysis;
